import React, { useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

declare global {
  interface ImportMeta {
    env: {
      VITE_STRIPE_TABLE_ID: string;
      VITE_STRIPE_PUBLISHABLE_KEY: string;
    }
  }
}

interface TokenBarProps {
  freeLimit: number;
  purchasedTokens: number;
  lifetimeTotal: number;
  userId?: string;
}

const TokenBar: React.FC<TokenBarProps> = ({ freeLimit, purchasedTokens, lifetimeTotal, userId }) => {
  const totalAvailable = freeLimit + purchasedTokens;
  const used = lifetimeTotal;
  const remaining = Math.max(0, totalAvailable - used);

  // Add Stripe script on component mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleGetMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Create modal backdrop
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.75)';
    modal.style.display = 'flex';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';
    modal.style.zIndex = '1000';

    // Create table container
    const tableContainer = document.createElement('div');
    tableContainer.style.maxWidth = '1000px';
    tableContainer.style.width = '90%';
    tableContainer.style.maxHeight = '90vh';
    tableContainer.style.overflow = 'auto';
    tableContainer.style.position = 'relative';
    tableContainer.style.backgroundColor = '#fff';
    tableContainer.style.borderRadius = '8px';
    tableContainer.style.padding = '20px';

    // Add close button
    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.style.position = 'sticky';
    closeButton.style.top = '20px';
    closeButton.style.right = '20px';
    closeButton.style.fontSize = '24px';
    closeButton.style.color = '#666';
    closeButton.style.background = 'none';
    closeButton.style.border = 'none';
    closeButton.style.cursor = 'pointer';
    closeButton.style.float = 'right';
    closeButton.style.zIndex = '1001';
    closeButton.onclick = () => document.body.removeChild(modal);

    // Create Stripe pricing table element
    const pricingTable = document.createElement('stripe-pricing-table');
    if (!import.meta.env.VITE_STRIPE_TABLE_ID || !import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
        console.error('Stripe configuration missing');
        return;
    }
    pricingTable.setAttribute('pricing-table-id', import.meta.env.VITE_STRIPE_TABLE_ID);
    pricingTable.setAttribute('publishable-key', import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
    
    // Add client reference ID if userId is available
    if (userId) {
        pricingTable.setAttribute('client-reference-id', userId);
    }

    // Configure success URL to return to the app
    const successUrl = new URL('/payment-success', window.location.origin);
    successUrl.searchParams.set('session_id', '{CHECKOUT_SESSION_ID}');
    pricingTable.setAttribute('success-url', successUrl.toString());

    // Assemble the modal
    tableContainer.appendChild(closeButton);
    tableContainer.appendChild(pricingTable);
    modal.appendChild(tableContainer);
    document.body.appendChild(modal);
  };

  const data = [{
    name: 'Tokens',
    used,
    remaining,
  }];

  return (
    <div className="flex items-center">
      <div style={{ width: '100%', height: 24 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            barSize={8}
            barGap={0}
          >
            <XAxis type="number" domain={[0, totalAvailable]} hide />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              formatter={(value: number, name: string) => [value.toLocaleString(), name]}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                border: 'none',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '12px',
                fontFamily: 'system-ui',
                color: 'white',
                lineHeight: '1'
              }}
              cursor={false}
              separator=": "
            />
            <Bar 
              dataKey="used" 
              fill="#ef4444" 
              stackId="stack" 
              name="Used" 
              radius={[4, 4, 4, 4]}
            />
            <Bar 
              dataKey="remaining" 
              fill="#4ade80" 
              stackId="stack" 
              name="Remaining"
              radius={[4, 4, 4, 4]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <button 
        onClick={handleGetMoreClick}
        className="text-sm text-blue-500 hover:text-blue-600 ml-2"
        disabled={!userId}
        style={{ opacity: userId ? 1 : 0.5, cursor: userId ? 'pointer' : 'not-allowed' }}
      >
        Get More
      </button>
    </div>
  );
};

export default TokenBar; 